/**
 * Design panel tile thumbnails, read from the CMS library.
 *
 * Styles, wrappers and form sets are authored as folderless library rows
 * (`GET /assets?kind=Style|Wrapper|Form`) — not catalog packs or team
 * folders, so CatalogIndex never holds them. Each row names the sandbox entry
 * it depicts in its metadata: Style `slug`, Wrapper `wrapperId`, Form `set`.
 * That id is the join; the row title is the fallback for a row authored
 * without it. An entry with no matching row keeps the tile's own fallback
 * (the style swatch, the letter square).
 */

import { Assets, EntityType, type AssetResponseDto } from 'trace-viewer/cms';
import { apiConfig, resolveUrl } from './catalog-api';
import { ensureFreshToken } from './catalog-auth';

export type LibraryThumbnailKind = 'style' | 'wrapper' | 'form';

/** Each kind's CMS kind, and the metadata key holding the sandbox id. */
const KINDS: Record<LibraryThumbnailKind, { entityType: EntityType; idKey: string }> = {
    style: { entityType: EntityType.Style, idKey: 'slug' },
    wrapper: { entityType: EntityType.Wrapper, idKey: 'wrapperId' },
    form: { entityType: EntityType.Form, idKey: 'set' },
};
const KIND_NAMES = Object.keys(KINDS) as LibraryThumbnailKind[];

export interface LibraryThumbnails {
    /**
     * The thumbnail for a library entry: the row whose metadata carries `id`,
     * else a row titled like `id` or one of `names`. Undefined = no row.
     */
    get(kind: LibraryThumbnailKind, id: string, names?: readonly string[]): string | undefined;
}

export const NO_LIBRARY_THUMBNAILS: LibraryThumbnails = { get: () => undefined };

/** Case, spaces and punctuation dropped: "Linear Array" meets "LinearArray", "Modern Gothic" meets "modern-gothic". */
const nameKey = (name: string): string => name.toLowerCase().replace(/[^a-z0-9]/g, '');

/** A string field of a row's metadata, which the API sends as a JSON string. */
function metadataString(raw: unknown, key: string): string | undefined {
    let parsed: unknown = raw;
    if (typeof raw === 'string') {
        try { parsed = JSON.parse(raw); } catch { return undefined; }
    }
    if (!parsed || typeof parsed !== 'object') return undefined;
    const value = (parsed as Record<string, unknown>)[key];
    return typeof value === 'string' && value ? value : undefined;
}

/**
 * Index CMS rows by kind. Rows without a thumbnail are skipped; the first row
 * per id or title wins. A row that carries its id is found by that id alone:
 * its title is only for rows authored without one, or an entry with no row of
 * its own could pick up the thumbnail of a row that is titled like it.
 */
export function indexLibraryThumbnails(
    rows: Partial<Record<LibraryThumbnailKind, readonly AssetResponseDto[]>>,
): LibraryThumbnails {
    const byId = new Map<string, string>();
    const byName = new Map<string, string>();
    for (const kind of KIND_NAMES) {
        for (const row of rows[kind] ?? []) {
            const url = resolveUrl(row.thumbnailUrl);
            if (!url) continue;
            const id = metadataString(row.metadata, KINDS[kind].idKey);
            if (id) {
                if (!byId.has(`${kind}:${id}`)) byId.set(`${kind}:${id}`, url);
                continue;
            }
            const name = row.title ? nameKey(row.title) : '';
            if (name && !byName.has(`${kind}:${name}`)) byName.set(`${kind}:${name}`, url);
        }
    }
    return {
        get(kind, id, names = []) {
            const exact = byId.get(`${kind}:${id}`);
            if (exact) return exact;
            for (const name of [id, ...names]) {
                const url = byName.get(`${kind}:${nameKey(name)}`);
                if (url) return url;
            }
            return undefined;
        },
    };
}

/**
 * List each kind with `list` and index the rows. A kind the CMS refuses (an
 * older backend 400s on `kind=Wrapper`) contributes nothing; only a failure
 * of every kind throws.
 */
export async function fetchLibraryThumbnails(
    list: (entityType: EntityType) => Promise<AssetResponseDto[]>,
): Promise<LibraryThumbnails> {
    const results = await Promise.allSettled(KIND_NAMES.map(kind => list(KINDS[kind].entityType)));
    const rows: Partial<Record<LibraryThumbnailKind, AssetResponseDto[]>> = {};
    results.forEach((result, i) => {
        if (result.status === 'fulfilled') rows[KIND_NAMES[i]] = result.value;
        else console.warn(`[DesignPanel] no ${KIND_NAMES[i]} thumbnails from the CMS:`, result.reason);
    });
    if (Object.keys(rows).length === 0) throw new Error('The CMS library did not list any kind.');
    return indexLibraryThumbnails(rows);
}

let cached: Promise<LibraryThumbnails> | null = null;

/**
 * The library's thumbnails, once per page load. Library rows are
 * folderless and the same for every team, so the first successful load is
 * kept. Signed out = none yet (call again after sign-in); a failed load is
 * retried on the next call.
 */
export async function loadLibraryThumbnails(): Promise<LibraryThumbnails> {
    if (cached) return cached;
    const token = await ensureFreshToken();
    if (!token) return NO_LIBRARY_THUMBNAILS;
    if (!cached) {
        const client = new Assets(apiConfig(token));
        cached = fetchLibraryThumbnails(async kind => (await client.assetsList({ Kind: kind })).data ?? []);
        cached.catch(() => { cached = null; });
    }
    return cached;
}
